import React, { useState } from "react";
import { Copy, Download, ExternalLink, Utensils, ShoppingBag, Truck, Hash } from "lucide-react";
import { toast } from "sonner";

function QRBlock({ url, title, subtitle, tone, testid }) {
  const qrColor = tone === "orange" ? "C85A32" : tone === "green" ? "3B503A" : "231F1D";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=15&data=${encodeURIComponent(url)}&color=${qrColor}&bgcolor=FBF9F5`;
  const Icon = tone === "orange" ? ShoppingBag : tone === "green" ? Truck : Utensils;
  const border = tone === "orange" ? "border-[#C85A32]/40" : tone === "green" ? "border-[#3B503A]/40" : "border-[#E8E2D5]";
  const iconBg = tone === "orange" ? "bg-[#C85A32]" : tone === "green" ? "bg-[#3B503A]" : "bg-[#231F1D]";
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success(`Link ${title} copiato`); };
  return (
    <div data-testid={testid} className={`rounded-2xl bg-white border-2 ${border} p-4 shadow-sm`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-full ${iconBg} text-white grid place-items-center`}><Icon size={16}/></div>
        <div className="min-w-0">
          <div className="font-serif-display font-bold text-lg leading-tight">{title}</div>
          <div className="text-[11px] text-[#6B625B]">{subtitle}</div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-[#FBF9F5] rounded-xl p-3 border border-[#F0EBE1]">
        <img src={qrUrl} alt={`QR ${title}`} className="w-40 h-40"/>
      </div>
      <div className="mt-2 rounded-xl bg-[#F0EBE1] p-2 text-[10px] font-mono text-[#231F1D] break-all">{url}</div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <button data-testid={`${testid}-copy`} onClick={copy} className="border border-[#E8E2D5] rounded-full py-1.5 text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F0EBE1]"><Copy size={11}/> Copia</button>
        <a data-testid={`${testid}-download`} href={qrUrl} download={`qr-${title.toLowerCase()}.png`} className="border border-[#E8E2D5] rounded-full py-1.5 text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F0EBE1]"><Download size={11}/> PNG</a>
        <a data-testid={`${testid}-preview`} href={url} target="_blank" rel="noreferrer" className={`${iconBg} text-white rounded-full py-1.5 text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:brightness-110`}><ExternalLink size={11}/> Prova</a>
      </div>
    </div>
  );
}

export default function QRPanel({ restaurant }) {
  const [tableNum, setTableNum] = useState("");
  if (!restaurant) return null;
  const base = `${window.location.origin}/menu/${restaurant.slug}`;
  const takeawayEnabled = restaurant.takeaway_enabled === true;
  const deliveryEnabled = restaurant.delivery_enabled === true;
  const tableUrl = tableNum.trim() ? `${base}?table=${encodeURIComponent(tableNum.trim())}` : null;

  return (
    <div className="bg-white rounded-3xl border border-[#E8E2D5] p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">QR del tuo locale</div>
      <h3 className="font-serif-display text-2xl font-bold mt-1">Sempre lo stesso QR · Menu aggiornato dalla voce</h3>
      <p className="text-sm text-[#6B625B] mt-1">Stampa uno o più QR distinti per Sala, Asporto e Delivery. Puoi generare anche un <b>QR per singolo tavolo</b> per velocizzare la registrazione.</p>
      <div className="mt-5 space-y-4">
        <QRBlock testid="qr-dine-in" tone="dark" title="Sala" subtitle="Il cliente inserisce il numero tavolo" url={base}/>

        {/* QR per tavolo (auto-fill) */}
        <div data-testid="qr-table-block" className="rounded-2xl bg-gradient-to-br from-[#3B503A]/10 to-white border-2 border-dashed border-[#3B503A]/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#3B503A] text-white grid place-items-center"><Hash size={14}/></div>
            <div className="min-w-0 flex-1">
              <div className="font-serif-display font-bold text-lg leading-tight">QR per tavolo</div>
              <div className="text-[11px] text-[#6B625B]">Il numero è già inserito · il cliente compila solo nome ed email</div>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="qr-table-input"
              value={tableNum}
              onChange={(e) => setTableNum(e.target.value.replace(/[^0-9A-Za-z-]/g, ""))}
              placeholder="Es. 5, 12, A3..."
              className="flex-1 rounded-full border border-[#E8E2D5] bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B503A]"
            />
            {tableUrl && (
              <button
                data-testid="qr-table-clear"
                onClick={() => setTableNum("")}
                className="px-4 py-2 rounded-full border border-[#E8E2D5] text-[11px] font-bold text-[#6B625B] hover:bg-[#F0EBE1]"
              >
                Reset
              </button>
            )}
          </div>
          {tableUrl ? (
            <div className="mt-3">
              <QRBlock testid={`qr-table-${tableNum}`} tone="green" title={`Tavolo ${tableNum}`} subtitle={`Auto-fill · Tavolo #${tableNum} pre-compilato`} url={tableUrl}/>
            </div>
          ) : (
            <div className="mt-3 rounded-xl bg-[#EAF0E9] p-3 text-[11px] text-[#3B503A]">
              💡 Inserisci un numero (o codice) tavolo per generare il QR dedicato. Stampalo e attaccalo al tavolo: chi lo scansiona vedrà il tavolo già pre-compilato in fase di registrazione.
            </div>
          )}
        </div>

        {takeawayEnabled && <QRBlock testid="qr-takeaway" tone="orange" title="Asporto" subtitle="Ordine da ritirare · Pagamento in contanti" url={`${base}?mode=takeaway`}/>}
        {deliveryEnabled && <QRBlock testid="qr-delivery" tone="green" title="Delivery" subtitle="Consegna a domicilio · Contanti alla consegna" url={`${base}?mode=delivery`}/>}
        {!takeawayEnabled && !deliveryEnabled && (
          <div className="rounded-2xl bg-[#FEF3D6] border border-[#D99B26]/40 p-3 text-[12px] text-[#8C580C]">
            💡 Attiva Asporto o Delivery dalle impostazioni per generare i QR code dedicati. Il cliente sceglie modalità e paga in contanti alla consegna/ritiro.
          </div>
        )}
      </div>
    </div>
  );
}
